import { Icon } from '@chakra-ui/react';
import { IconButtonWithTooltip, Input, Switch, Td, Text, Tr } from '@elkaso-app/web-design';
import type { DealCategoryTreeNode } from 'apis/exclusive-deals';
import { FiChevronDown, FiChevronUp, FiTrash2 } from 'react-icons/fi';
import { CategoryCell } from './category-cell';
import type { EditableDealRow } from './editable-deal-row.types';
import { ProductSelectCell } from './product-select-cell';
import { SupplierSelectCell } from './supplier-select-cell';

interface Props {
  row: EditableDealRow;
  index: number;
  invalid: boolean;
  categoryTree: DealCategoryTreeNode[];
  onChange: (clientId: string, patch: Partial<EditableDealRow>) => void;
  onInsert: (index: number, position: 'above' | 'below') => void;
  onRemove: (index: number) => void;
}

// Readonly product-derived cell: autofilled whenever the row's product changes.
const ProductInfoCell = ({ value }: { value: string | undefined }) => (
  <Td>
    <Text fontSize='xs' color='gray.600' whiteSpace='nowrap'>
      {value || '-'}
    </Text>
  </Td>
);

export const EditableDealRowView = ({ row, index, invalid, categoryTree, onChange, onInsert, onRemove }: Props) => {
  const supplierId = row.supplier?.id ?? null;
  const canRemove = row.id === undefined; // only new rows are removable

  return (
    <Tr bg={invalid ? 'red.50' : undefined}>
      <Td>
        <SupplierSelectCell
          value={row.supplier}
          isInvalid={invalid && !row.supplier}
          onChange={(supplier) => onChange(row.clientId, { supplier, product: null })}
        />
      </Td>
      <Td>
        <ProductSelectCell
          supplierId={supplierId}
          dealId={row.id}
          value={row.product}
          isInvalid={invalid && !row.product}
          onChange={(product) => onChange(row.clientId, { product })}
        />
      </Td>
      <ProductInfoCell value={row.product?.defaultUnit} />
      <ProductInfoCell value={row.product?.brandEn} />
      <ProductInfoCell value={row.product?.origin} />
      <ProductInfoCell value={row.product?.state} />
      <ProductInfoCell value={row.product?.specs} />
      <CategoryCell
        product={row.product}
        tree={categoryTree}
        isInvalid={invalid && row.product?.catalogCategoryId == null}
        onSelect={(category) =>
          row.product &&
          onChange(row.clientId, {
            product: { ...row.product, catalogCategoryId: category.id, categoryTitle: category.title },
          })
        }
      />
      <Td>
        <Input
          type='number'
          step='0.01'
          size='sm'
          w='90px'
          value={row.dealPrice}
          isInvalid={invalid && (row.dealPrice === '' || Number(row.dealPrice) <= 0)}
          onChange={(e) => onChange(row.clientId, { dealPrice: e.target.value })}
        />
      </Td>
      <Td>
        <Input
          type='number'
          step='0.01'
          size='sm'
          w='72px'
          value={row.cashbackPercent}
          onChange={(e) => onChange(row.clientId, { cashbackPercent: e.target.value })}
        />
      </Td>
      <Td textAlign='center'>
        <Switch
          size='sm'
          isChecked={row.isActive}
          onChange={(e) => onChange(row.clientId, { isActive: e.target.checked })}
        />
      </Td>
      <Td whiteSpace='nowrap'>
        <IconButtonWithTooltip
          size='xs'
          variant='ghost'
          label='Add row above'
          aria-label='add-row-above'
          Icon={<Icon as={FiChevronUp} />}
          onClick={() => onInsert(index, 'above')}
        />
        <IconButtonWithTooltip
          size='xs'
          variant='ghost'
          label='Add row below'
          aria-label='add-row-below'
          Icon={<Icon as={FiChevronDown} />}
          onClick={() => onInsert(index, 'below')}
        />
        {canRemove && (
          <IconButtonWithTooltip
            size='xs'
            variant='ghost'
            colorScheme='red'
            label='Remove row'
            aria-label='remove-row'
            Icon={<Icon as={FiTrash2} />}
            onClick={() => onRemove(index)}
          />
        )}
      </Td>
    </Tr>
  );
};
